import {
  Users, CheckCircle2, MailCheck, LineChart, ShieldCheck, GraduationCap, QrCode, CalendarCheck,
  Heart, Clock, Tablet, UserPlus, Sparkles, BookOpen, Layers, Bell, MessageCircle, Image, Globe2, Flag,
} from "lucide-react";

export const TOOLS = [
  {
    slug: "tracker",
    name: "Tracker",
    brand: "thedisciple.tracker",
    accent: "#E6B85C",
    glow: "rgba(230,184,92,0.15)",
    kicker: "Bible-study CRM",
    headline: "No guest slips through the cracks again.",
    pitch: "Walk every first-time guest from their first visit to Next Steps graduation. Coordinators see the whole pipeline; home-study teachers see exactly who is theirs and what's next.",
    appLink: "/crm/login",
    included: "Included in every plan",
    features: [
      { icon: Users, title: "One church directory", body: "Guests, disciples, members and teachers live in one place, with a duplicate warning so nobody gets entered twice." },
      { icon: GraduationCap, title: "First Steps → Next Steps", body: "Track the Introduction, the home Bible study and the 24-lesson Next Steps class session by session, with notes." },
      { icon: MailCheck, title: "Teacher portal", body: "Teachers get their own login with their guests, lesson files and one-tap progress check-offs." },
      { icon: LineChart, title: "Team activity", body: "A weekly roll-up shows who was contacted, who's stalled and which teachers need help." },
      { icon: BookOpen, title: "Lesson library", body: "Teacher notes, slides, student sheets and videos are attached to every lesson, ready when you need them." },
      { icon: QrCode, title: "QR sign-up and inquiries", body: "Guests sign themselves up from a QR code, and you approve them from a review queue." },
    ],
    steps: [
      { title: "Add the guest", body: "From a QR sign-up, the kiosk, or the directory, in seconds." },
      { title: "Assign a teacher", body: "Pair them with a home-study teacher (or a husband-and-wife team)." },
      { title: "Watch them grow", body: "Every lesson, note and milestone is logged until they graduate." },
    ],
    faq: [
      { q: "Do I need to be technical to set this up?", a: "No. Most churches are running in an afternoon. We'll walk you through it on your demo call." },
      { q: "How many teachers and coordinators can I add?", a: "As many as you need. Your plan is sized by the number of people in your directory, not by user seats." },
      { q: "Can I use my own Bible-study curriculum?", a: "Yes. The lesson library holds links and files for every lesson, so your material comes along with you." },
      { q: "Is our data private to our church?", a: "Yes. Each church is fully separated, and only the people you invite can see your directory." },
    ],
  },
  {
    slug: "attendance",
    name: "Attendance",
    brand: "thedisciple.attendance",
    accent: "#3B82F6",
    glow: "rgba(59,130,246,0.15)",
    kicker: "Church-body attendance",
    headline: "Know who's missing, not just how many came.",
    pitch: "Split your congregation into sections, give each section leader their own login, and take attendance service by service, or let people check themselves in with a QR code.",
    appLink: "/attendance/login",
    included: "Add-on",
    features: [
      { icon: Layers, title: "Sections and leaders", body: "Divide the body into sections with a shepherd for each, who sees only their own people." },
      { icon: QrCode, title: "QR self check-in", body: "One permanent QR poster. Check-in opens and closes automatically around each service." },
      { icon: CalendarCheck, title: "Recurring services", body: "Weekly, bi-weekly or monthly. Sunday AM, Wednesday Bible study, whatever you hold." },
      { icon: Users, title: "Families check in together", body: "Parents check in the whole household with one tap." },
      { icon: LineChart, title: "Coordinator overview", body: "See every section at a glance and spot the people who have quietly drifted away." },
      { icon: ShieldCheck, title: "Shared directory", body: "Attendance uses the same directory as the Tracker, so you never have to re-type a name." },
    ],
    steps: [
      { title: "Build your sections", body: "Group your members and assign a section leader." },
      { title: "Post the QR", body: "People check in on their phones, or leaders check them off." },
      { title: "Follow up", body: "Leaders see who was absent and reach out that week." },
    ],
    faq: [
      { q: "Do members need an app or an account?", a: "No. They scan the QR code, find their name, and they're checked in." },
      { q: "Can section leaders see other sections?", a: "No. Leaders only see their own people. Coordinators see everyone." },
      { q: "What if someone forgets to check in?", a: "Section leaders can mark anyone present from their own screen at any time." },
    ],
  },
  {
    slug: "prayer",
    name: "Prayer",
    brand: "thedisciple.prayer",
    accent: "#EC4899",
    glow: "rgba(236,72,153,0.15)",
    kicker: "Prayer log",
    headline: "See the people quietly carrying your church.",
    pitch: "A one-tap prayer log for the sanctuary between services. Members find their name, tap once, and go pray, while leadership gets a faithful picture of the church's prayer life.",
    appLink: "/prayer-admin",
    included: "Add-on",
    features: [
      { icon: Heart, title: "One-tap check-in", body: "No timers and no checkout. Just a simple record that someone came to pray." },
      { icon: QrCode, title: "Its own prayer QR", body: "Kept separate from attendance, so prayer is never counted as a service." },
      { icon: Clock, title: "24-hour prayer list", body: "A rolling list of who has prayed encourages others to come." },
      { icon: Sparkles, title: "Prayer requests", body: "Members can attach a short request when they check in." },
      { icon: LineChart, title: "Reports and log", body: "Admin dashboard, full history, reports and manual entry." },
      { icon: ShieldCheck, title: "Shared directory", body: "Uses the same people and permissions as the rest of your tools." },
    ],
    steps: [
      { title: "Post the prayer QR", body: "Put it in the prayer room or sanctuary." },
      { title: "Members tap in", body: "They find their name, confirm, and go pray." },
      { title: "Leaders see the picture", body: "Spot the faithful intercessors and encourage the rest." },
    ],
    faq: [
      { q: "Is the prayer list public?", a: "Only first names and initials appear on the 24-hour list. Full details are for leadership only." },
      { q: "Can we use a tablet instead of phones?", a: "Yes. Pair it with Kiosk mode on a tablet in the prayer room." },
    ],
  },
  {
    slug: "kiosk",
    name: "Kiosk",
    brand: "thedisciple.kiosk",
    accent: "#10B981",
    glow: "rgba(16,185,129,0.15)",
    kicker: "Lobby tablet",
    headline: "Your welcome desk, open every service.",
    pitch: "Turn any tablet into a lobby kiosk. Guests sign themselves up, members check in for the service or for prayer, and every tap lands in your directory.",
    appLink: "/kiosk-launch",
    included: "Add-on",
    features: [
      { icon: Tablet, title: "Any tablet works", body: "Open a link, lock it in full-screen mode, and set it on the welcome table." },
      { icon: UserPlus, title: "Guest sign-up", body: "First-time guests enter their info, and you approve them from the review queue." },
      { icon: CalendarCheck, title: "Service check-in", body: "Members and families check in for the current service." },
      { icon: Heart, title: "Prayer check-in", body: "The same kiosk logs prayer visits between services." },
      { icon: Bell, title: "Big, friendly buttons", body: "Designed for every age, with large touch targets and clear words." },
      { icon: ShieldCheck, title: "Nothing to install", body: "Runs in the browser and updates automatically." },
    ],
    steps: [
      { title: "Open the kiosk link", body: "From your dashboard, on any tablet." },
      { title: "Set it out", body: "Put it in the lobby, on the welcome desk or in the prayer room." },
      { title: "Let people tap", body: "Sign-ups and check-ins flow straight into your tools." },
    ],
    faq: [
      { q: "What tablet do I need?", a: "Any iPad, Android or Fire tablet with a modern browser." },
      { q: "Can a guest see other people's information?", a: "No. The kiosk only shows names to confirm identity and never shows contact details." },
    ],
  },
  {
    slug: "social",
    name: "Social",
    brand: "thedisciple.social",
    accent: "#A78BFA",
    glow: "rgba(167,139,250,0.15)",
    kicker: "Church social network",
    headline: "A social feed that actually builds up the body.",
    pitch: "A private feed for each church plus a Network of public posts from churches everywhere. Completely free for everyone: post, share photos and articles, react, and comment.",
    appLink: "/social",
    included: "Free for every church",
    free: true,
    features: [
      { icon: Users, title: "Your church's own feed", body: "Only people who joined your church see its private posts." },
      { icon: Globe2, title: "The Network", body: "Public posts from churches everywhere in one encouraging feed." },
      { icon: Heart, title: "Reactions people know", body: "Like, Love, Praying, Amen, Haha, Wow and Sad, all one tap away." },
      { icon: Image, title: "Photos and articles", body: "Anyone can share pictures, updates and long-form articles." },
      { icon: MessageCircle, title: "Comments", body: "Encourage each other under any post." },
      { icon: Flag, title: "Moderation built in", body: "Anyone can report a post, and church admins and coordinators can hide or remove it." },
    ],
    steps: [
      { title: "Share your invite", body: "Coordinators copy the church invite link from the Directory." },
      { title: "People join free", body: "Create an account and tap join." },
      { title: "Share and encourage", body: "Post, react and comment. Everything is free." },
    ],
    faq: [
      { q: "Does the church pay for Social?", a: "No. Social is free for every church and switched on automatically." },
      { q: "Is there anything to pay for?", a: "No. Every feature is free. An optional $4.99/mo membership lets you support the platform." },
      { q: "Who can see our church's posts?", a: "Posts are private to your church unless the author marks them Public, which also shares them to the Network." },
    ],
  },
];

export const TOOL_BY_SLUG = Object.fromEntries(TOOLS.map((t) => [t.slug, t]));

export const PRICING_TIERS = [
  { label: "Tier 1", range: "Up to 50 people", tracker: 19, addon: 4.99 },
  { label: "Tier 2", range: "51 – 250 people", tracker: 29, addon: 9.99 },
  { label: "Tier 3", range: "251 – 1,000 people", tracker: 39, addon: 14.99 },
  { label: "Tier 4", range: "1,001 – 2,000 people", tracker: 59, addon: 19.99 },
];
